import { Component, OnDestroy, OnInit } from '@angular/core';
import { CustomerModel } from '@models/customer.model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastService } from '@services/ui';
import { PaymentAgreementService } from '@modules/payment-agreement/services/payment-agreement.service';
import { Subject, take } from 'rxjs';
import { mapFilterCopies } from '@shared/components/joint-js/helpers';
import { CopiesService } from '@modules/copies/service/copies.service';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-partial-payment-offer',
  templateUrl: './partial-payment-offer.component.html',
  styleUrls: ['./partial-payment-offer.component.scss'],
})
export class PartialPaymentOfferComponent implements OnInit, OnDestroy {
  instance: any;
  customer: CustomerModel = {} as CustomerModel;

  amountMode: 'EUR' | '%' = 'EUR';
  private minAmountEur = 0;
  private minAmountPct = 100;
  selectedChannel = '';
  selectedCopyId = '';
  sms = '';
  email = '';
  hasSms = false;
  hasEmail = false;
  copiesOptions: { label: string; value: string }[] = [];

  loading = false;
  totalAmount = 0;
  private copies: any[] = [];
  destroy$ = new Subject<void>();

  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private paymentAgreementService: PaymentAgreementService,
    private copiesService: CopiesService,
    private toastService: ToastService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
  ) {
    this.instance = this.config.data.instance;
    this.customer = this.config.data.customer;
    this.totalAmount = this.instance.totalAmount?.amount
      ? this.instance.totalAmount.amount / 100
      : this.instance.totalAmount / 100;
    this.minAmountEur = this.totalAmount;
  }

  ngOnInit(): void {
    this.initContactChannels();
    this.getCopyList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initContactChannels(): void {
    this.hasSms = Array.isArray(this.customer.phones) && this.customer.phones.length > 0;
    this.hasEmail =
      Array.isArray(this.customer.emailAddresses) && this.customer.emailAddresses.length > 0;
    if (this.hasSms) {
      this.selectedChannel = 'sendSMS';
      this.sms = this.customer.phones?.[0] ?? '';
    } else if (this.hasEmail) {
      this.selectedChannel = 'sendEmail';
      this.email = this.customer.emailAddresses?.[0] ?? '';
    }
  }

  getCopyList(): void {
    this.loading = true;
    this.copiesService
      .searchCopies()
      .pipe(take(1))
      .subscribe((response: any) => {
        this.copies = response.map((copy: any) => ({
          id: copy.id,
          name: copy.name,
          type: copy.type,
        }));
        this.loading = false;
        if (this.selectedChannel) {
          this.filterCopiesForChannel(this.selectedChannel);
        }
      });
  }

  selectChannel(channel: string): void {
    this.selectedChannel = channel;
    this.selectedCopyId = '';
    if (channel === 'sendSMS') {
      this.sms = this.customer.phones?.[0] ?? '';
      this.email = '';
    } else {
      this.email = this.customer.emailAddresses?.[0] ?? '';
      this.sms = '';
    }
    this.filterCopiesForChannel(channel);
  }

  private filterCopiesForChannel(channel: string): void {
    const channelType = mapFilterCopies(channel);
    this.copiesOptions = this.copies
      .filter((copy: any) => copy.type === channelType)
      .map((copy: any) => ({ label: copy.name, value: copy.id }));
    this.selectedCopyId = this.copiesOptions[0]?.value ?? '';
  }

  get minAmount(): number {
    return this.amountMode === 'EUR' ? this.minAmountEur : this.minAmountPct;
  }

  set minAmount(value: number) {
    if (this.amountMode === 'EUR') {
      this.minAmountEur = value;
    } else {
      this.minAmountPct = value;
    }
  }

  setAmountMode(mode: 'EUR' | '%'): void {
    this.amountMode = mode;
  }

  cancel(): void {
    this.ref.close();
  }

  checkDisabled(): boolean {
    return !this.selectedChannel || !this.selectedCopyId || !this.minAmount || this.minAmount <= 0;
  }

  send(): void {
    const absoluteAmount = this.amountMode === 'EUR' ? this.minAmount : 0;
    const percentageAmount = this.amountMode === '%' ? this.minAmount : 0;

    this.paymentAgreementService
      .sendPartialPaymentOffer(
        this.instance.customerCaseId || this.instance.id,
        this.selectedCopyId,
        new Date(new Date().getTime() + 72 * 60 * 60 * 1000),
        this.currency,
        absoluteAmount,
        percentageAmount,
      )
      .subscribe((res: any) => {
        if (res !== null) {
          this.toastService.showSuccess(
            this.translate.instant(
              'DATA.MODULES.SHARED.COMPONENTS.PARTIAL_PAYMENT.SUCCESS_TITLE',
            ),
            this.translate.instant(
              'DATA.MODULES.SHARED.COMPONENTS.PARTIAL_PAYMENT.SUCCESS_MESSAGE',
            ),
          );
          setTimeout(() => this.ref.close(res));
        } else {
          this.toastService.showError(
            this.translate.instant(
              'DATA.MODULES.SHARED.COMPONENTS.PARTIAL_PAYMENT.ERROR_TITLE',
            ),
            this.translate.instant(
              'DATA.MODULES.SHARED.COMPONENTS.PARTIAL_PAYMENT.ERROR_MESSAGE',
            ),
          );
        }
      });
  }

  get currency(): string {
    return (this.instance.totalAmountCurrency || this.instance.totalAmount?.currency || '')
      .slice(0, 3)
      .toUpperCase();
  }

  get customerName(): string {
    return this.instance.customerName || this.customer.longName || this.customer.name || '';
  }

  get caseName(): string {
    return this.instance.name || this.instance.customerCaseId || this.instance.id || '';
  }

  get totalAmountFormatted(): string {
    return new Intl.NumberFormat('es', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    }).format(this.totalAmount);
  }

  get equivalentPercent(): number {
    if (!this.totalAmount) return 0;
    return (this.minAmount / this.totalAmount) * 100;
  }

  get equivalentAmount(): number {
    return (this.totalAmount * this.minAmount) / 100;
  }

  get equivHtml(): SafeHtml {
    if (this.amountMode === 'EUR') {
      const pct = this.equivalentPercent.toFixed(2).replace('.', ',');
      return this.sanitizer.bypassSecurityTrustHtml(
        this.translate.instant(
          'DATA.MODULES.SHARED.COMPONENTS.PARTIAL_PAYMENT.EQUIV_PCT_HINT',
          { pct },
        ),
      );
    } else {
      const amount = new Intl.NumberFormat('es', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(this.equivalentAmount);
      return this.sanitizer.bypassSecurityTrustHtml(
        this.translate.instant(
          'DATA.MODULES.SHARED.COMPONENTS.PARTIAL_PAYMENT.EQUIV_EUR_HINT',
          { amount, currency: this.currency },
        ),
      );
    }
  }

  get summaryHtml(): SafeHtml {
    const amountStr =
      this.amountMode === 'EUR'
        ? `${new Intl.NumberFormat('es', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(this.minAmount)} ${this.currency}`
        : `${this.minAmount}%`;
    const channel = this.selectedChannel === 'sendSMS' ? 'SMS' : 'Email';
    const html = this.translate.instant(
      'DATA.MODULES.SHARED.COMPONENTS.PARTIAL_PAYMENT.SUMMARY',
      { customerName: this.customerName, amount: amountStr, channel },
    );
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
